import { createMuiTheme } from '@material-ui/core/styles';

/**
 * Central application theme. Matches the tenant-branding defaults in
 * utils/tenantThemeCssVars.js (--tenant-color-primary #3B82F6) so MUI
 * components and CSS-var-driven chrome render one coherent palette.
 */
const theme = createMuiTheme({
  palette: {
    primary: {
      light: '#60A5FA',
      main: '#3B82F6',
      dark: '#2563EB',
      contrastText: '#ffffff',
    },
    secondary: {
      light: '#334155',
      main: '#0F172A',
      dark: '#020617',
      contrastText: '#ffffff',
    },
    error: {
      main: '#B91C1C',
    },
    success: {
      main: '#047857',
    },
    warning: {
      main: '#B45309',
    },
    text: {
      primary: '#111827',
      secondary: '#64748B',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h3: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h4: { fontSize: '1.35rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.3 },
    h5: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
    h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    subtitle1: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: 10,
  },
  props: {
    MuiButton: {
      disableElevation: true,
    },
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 10,
        padding: '6px 16px',
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: '#2563EB',
        },
      },
      outlined: {
        borderColor: '#E5E7EB',
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 12,
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 16,
        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '20px 24px 12px',
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: '1px solid var(--gray-100)',
        fontSize: 14,
      },
      head: {
        fontWeight: 600,
        fontSize: 12,
        color: 'var(--text-secondary)',
        background: 'var(--gray-50)',
        borderBottom: '1px solid var(--border)',
      },
    },
    MuiTableRow: {
      root: {
        '&:hover': {
          backgroundColor: 'var(--gray-50)',
        },
      },
    },
    MuiTooltip: {
      tooltip: {
        fontSize: '0.75rem',
        borderRadius: 8,
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        background: 'var(--surface)',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '& $notchedOutline': {
          borderColor: 'var(--border)',
        },
        '&:hover $notchedOutline': {
          borderColor: 'var(--border-strong)',
        },
        '&$focused $notchedOutline': {
          borderColor: 'var(--brand)',
          borderWidth: 1.5,
          boxShadow: '0 0 0 3px var(--brand-ring)',
        },
      },
      input: {
        fontSize: 14,
        padding: '10.5px 12px',
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: 13,
        color: 'var(--text-secondary)',
      },
    },
    MuiFormHelperText: {
      root: {
        fontSize: 12,
        '&$error': {
          color: 'var(--danger-strong)',
        },
      },
    },
    MuiMenu: {
      paper: {
        borderRadius: 10,
        border: '1px solid var(--border)',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
      },
    },
  },
});

export default theme;
